import { useEffect, useState } from "react";

import type { AdminListPagination, AdminUserSummary } from "@chaochud/contracts";

import { AuditTrail, CriticalActionForm, DataTable, PageHeader, StatusPill, toAuditTrailEntry } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";
import type { AuditTrailEntry } from "@/components/admin-ui";

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  ACTIVE: "success",
  SUSPENDED: "error",
  PENDING: "warning",
};

export function UsersPage() {
  const [items, setItems] = useState<AdminUserSummary[]>([]);
  const [pagination, setPagination] = useState<AdminListPagination | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<AdminUserSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditTrailEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = async (targetPage = page) => {
    try {
      const data = await adminOpsClient.listUsers(targetPage, q || undefined);
      setItems(data.items);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "โหลดไม่สำเร็จ");
    }
  };

  useEffect(() => {
    void load(page);
  }, [page]);

  useEffect(() => {
    if (!selected) {
      setAuditEntries([]);
      return;
    }
    setAuditLoading(true);
    adminOpsClient
      .listResourceAuditLogs("USER", selected.id)
      .then((data) => setAuditEntries(data.items.map(toAuditTrailEntry)))
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  }, [selected]);

  return (
    <div className="space-y-6">
      <PageHeader title="จัดการผู้ใช้งาน">
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            // Reset to page 1 for a new search — if already on page 1, `setPage(1)`
            // is a no-op that won't retrigger the load effect, so fetch directly.
            if (page === 1) {
              void load(1);
            } else {
              setPage(1);
            }
          }}
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นหาอีเมล/ชื่อ"
            className="rounded-lg border border-slate-line px-3.5 py-2 text-[13px]"
          />
          <button type="submit" className="rounded-lg bg-plum px-3.5 py-2 text-[13px] font-semibold text-white hover:opacity-90">
            ค้นหา
          </button>
        </form>
      </PageHeader>
      {error ? <p className="text-sm text-error-text">{error}</p> : null}
      <DataTable
        headers={["อีเมล", "ชื่อ", "สถานะ", "Roles", ""]}
        rows={items.map((user) => [
          user.email ?? "-",
          user.displayName ?? "-",
          <StatusPill key={`status-${user.id}`} tone={STATUS_TONE[user.status] ?? "neutral"}>
            {user.status}
          </StatusPill>,
          user.roles.join(", ") || "-",
          <button
            key={user.id}
            type="button"
            className="text-sm font-semibold text-plum hover:underline"
            onClick={() => setSelected(user)}
          >
            จัดการ
          </button>,
        ])}
        pagination={pagination ?? undefined}
        onPageChange={setPage}
      />
      {selected ? (
        <div className="grid gap-4 md:grid-cols-[1fr_280px]">
          <div className="space-y-3">
            <p className="text-sm font-medium text-ink">
              เลือก: {selected.email} ({selected.status})
            </p>
            {selected.status !== "SUSPENDED" ? (
              <CriticalActionForm
                title="ระงับผู้ใช้"
                targetLabel={selected.email ?? selected.id}
                currentState={selected.status}
                expectedState="SUSPENDED"
                impact="ผู้ใช้จะเข้าสู่ระบบแอปลูกค้า/ร้านไม่ได้"
                submitLabel="ยืนยันระงับ"
                onSubmit={async (payload) => {
                  const updated = await adminOpsClient.suspendUser(selected.id, payload);
                  setSelected(updated);
                  await load();
                }}
              />
            ) : (
              <CriticalActionForm
                title="ยกเลิกการระงับผู้ใช้"
                targetLabel={selected.email ?? selected.id}
                currentState={selected.status}
                expectedState="ACTIVE"
                impact="ผู้ใช้จะใช้งานบัญชีได้อีกครั้ง"
                submitLabel="ยืนยันเปิดใช้"
                onSubmit={async (payload) => {
                  const updated = await adminOpsClient.unsuspendUser(selected.id, payload);
                  setSelected(updated);
                  await load();
                }}
              />
            )}
            <button type="button" className="text-sm text-muted hover:underline" onClick={() => setSelected(null)}>
              ปิดแผง
            </button>
          </div>
          <AuditTrail entries={auditEntries} isLoading={auditLoading} />
        </div>
      ) : null}
    </div>
  );
}
